import { useState } from 'react';
import { Button, Group, TextInput, Alert, Loader, Text, Badge } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { DataTable } from 'mantine-datatable';
import { IconAlertCircle } from '@tabler/icons-react';
import HttpClient from '../helpers/HttpClient';
import { Match } from '../types/match.type';
import dayjs from 'dayjs';

function MatchDataLoader() {
  const [shortTeamName, setShortTeamName] = useState('UfcSvHallwang');
  const [longTeamName, setLongTeamName] = useState('UFC-SV-Hallwang');
  const [season, setSeason] = useState('Saison-2025-26');
  const [team, setTeam] = useState('KM');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [results, setResults] = useState<Match[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    setResults([]);
    try {
      const params: Record<string, string> = {};
      if (startDate) params.startDate = dayjs(startDate).toISOString();
      if (endDate) params.endDate = dayjs(endDate).toISOString();

      const client = new HttpClient<Match>(
        `/matchdata/${shortTeamName}/${longTeamName}/${season}/${team}`
      );
      const data = await client.getAll(params);
      setResults(data ?? []);
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden der Spieldaten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1>Spieldaten laden</h1>
      <Text c="dimmed" mb="md" size="sm">
        Lädt Spieldaten von vereine.oefb.at und speichert Spiele, Aufstellungen und Ereignisse.
        Dieser Vorgang kann einige Minuten dauern.
      </Text>

      <Group mb="xs" align="flex-end" wrap="wrap">
        <TextInput
          label="Kurzname (URL)"
          value={shortTeamName}
          onChange={e => setShortTeamName(e.currentTarget.value)}
          placeholder="ufc-sv-hallwang"
          w={180}
        />
        <TextInput
          label="Langname"
          value={longTeamName}
          onChange={e => setLongTeamName(e.currentTarget.value)}
          placeholder="UFC-SV-Hallwang"
          w={180}
        />
        <TextInput
          label="Saison"
          value={season}
          onChange={e => setSeason(e.currentTarget.value)}
          placeholder="2024-25"
          w={100}
        />
        <TextInput
          label="Team Nr."
          value={team}
          onChange={e => setTeam(e.currentTarget.value)}
          placeholder="1"
          w={80}
        />
      </Group>
      <Group mb="md" align="flex-end">
        <DateInput
          valueFormat="DD.MM.YYYY"
          label="Von"
          value={startDate}
          onChange={setStartDate}
          clearable
        />
        <DateInput
          valueFormat="DD.MM.YYYY"
          label="Bis"
          value={endDate}
          onChange={setEndDate}
          clearable
        />
        <Button onClick={load} loading={loading}>
          Laden
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle />} color="red" mb="md">
          {error}
        </Alert>
      )}

      {loading && (
        <Group>
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Spieldaten werden abgerufen, bitte warten…</Text>
        </Group>
      )}

      {results.length > 0 && (
        <>
          <Text fw={600} mb="xs">{results.length} Spiele importiert</Text>
          <DataTable
            withTableBorder
            borderRadius="sm"
            withColumnBorders
            striped
            highlightOnHover
            records={results}
            columns={[
              {
                accessor: 'matchDate',
                title: 'Datum',
                width: 110,
                render: ({ matchDate }) => dayjs(matchDate).format('DD.MM.YYYY'),
              },
              { accessor: 'homeTeamName', title: 'Heim' },
              { accessor: 'awayTeamName', title: 'Gast' },
              {
                accessor: 'homeGoals',
                title: 'Ergebnis',
                width: 90,
                textAlign: 'center',
                render: ({ homeGoals, awayGoals }) =>
                  homeGoals != null ? `${homeGoals} : ${awayGoals}` : '-',
              },
              { accessor: 'matchType', title: 'Typ', width: 90 },
              { accessor: 'season', title: 'Saison', width: 90 },
              {
                accessor: 'isCompleted',
                title: 'Status',
                width: 90,
                render: ({ isCompleted }) =>
                  isCompleted ? (
                    <Badge color="green" size="sm">Abgeschlossen</Badge>
                  ) : (
                    <Badge color="gray" size="sm">Offen</Badge>
                  ),
              },
            ]}
          />
        </>
      )}
    </>
  );
}

export default MatchDataLoader;
